import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@client/components/ui/dialog';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Label } from '@client/components/ui/label';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import { AlertCircle, Send, Wand2 } from 'lucide-react';
import type { DialogType } from './useSubscriptionSeats';
import { SeatExpirationCard, type ExpirationValue } from './SeatExpirationCard';
import { BillingImpactCard } from '@client/components/shared/BillingImpactCard';

interface InvitationPreview {
  advisorExists: boolean;
  advisorInfo?: {
    advisorId: string;
    advisorFirstName: string | null;
    advisorLastName: string | null;
    advisorEmail: string;
  };
  billingPreview: BillingImpact;
}

interface SeatDialogsProps {
  dialogType: DialogType;
  emailInput: string;
  setEmailInput: (value: string) => void;
  showPreview: boolean;
  setShowPreview: (value: boolean) => void;
  invitationPreviewStatus: 'idle' | 'loading' | 'success' | 'error';
  isPreviewingInvitation: boolean;
  invitationPreviewError: string | null;
  duplicateEmailError: string | null;
  invitationPreview: InvitationPreview | null;
  isMutating: boolean;
  currentMutation: string | null;
  isWizardUser?: boolean;
  expirationValue: ExpirationValue;
  onExpirationChange: (value: ExpirationValue) => void;
  onClose: () => void;
  onPreviewInvitation: () => void;
  onSendInvitation: () => void;
  onRemoveSeat: () => void;
  onDirectAssignSeat?: () => void;
}

export function SeatDialogs({
  dialogType,
  emailInput,
  setEmailInput,
  showPreview,
  setShowPreview,
  invitationPreviewStatus,
  isPreviewingInvitation,
  invitationPreviewError,
  duplicateEmailError,
  invitationPreview,
  isMutating,
  currentMutation,
  isWizardUser,
  expirationValue,
  onExpirationChange,
  onClose,
  onPreviewInvitation,
  onSendInvitation,
  onRemoveSeat,
  onDirectAssignSeat,
}: SeatDialogsProps) {
  return (
    <>
      {/* Invite Advisor Dialog */}
      <Dialog
        open={dialogType === 'addSeat'}
        onOpenChange={onClose}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Invite Advisor to Subscription</DialogTitle>
            <DialogDescription className="text-gray-700">
              {!showPreview
                ? "Enter the advisor's email to see billing impact and send an invitation."
                : 'Review the details below and confirm to send the invitation.'}
            </DialogDescription>
          </DialogHeader>

          {!showPreview ? (
            <>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label
                    htmlFor="email"
                    className="text-primary-dark"
                  >
                    Advisor Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="advisor@example.com"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && emailInput) {
                        onPreviewInvitation();
                      }
                    }}
                  />
                </div>
                {duplicateEmailError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{duplicateEmailError}</AlertDescription>
                  </Alert>
                )}
                {invitationPreviewError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{invitationPreviewError}</AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={onPreviewInvitation}
                  disabled={!emailInput || isPreviewingInvitation}
                >
                  {isPreviewingInvitation ? 'Loading...' : 'Preview Invitation'}
                </Button>
              </DialogFooter>
            </>
          ) : invitationPreviewStatus === 'loading' ? (
            // Skeleton loading state
            <div className="grid gap-4 py-4">
              <div className="rounded-md border p-4">
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-4 w-48" />
              </div>
              <div className="rounded-md border p-4">
                <Skeleton className="mb-3 h-4 w-24" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
              <Skeleton className="h-16 w-full rounded-md" />
            </div>
          ) : invitationPreview ? (
            <>
              <div className="grid gap-4 py-4">
                {/* Billing Impact */}
                <BillingImpactCard billingPreview={invitationPreview.billingPreview} />

                {/* Seat Expiration Settings */}
                <SeatExpirationCard
                  value={expirationValue}
                  onChange={onExpirationChange}
                  defaultOpen={false}
                />

                {/* Invitation Email */}
                <div className="bg-primary-50 border-primary-100 rounded-md border p-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {invitationPreview.advisorExists ? (
                        <span className="bg-primary/10 text-primary inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium">
                          <div className="bg-primary h-1.5 w-1.5 rounded-full" />
                          Existing Advisor
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                          <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          New Advisor
                        </span>
                      )}
                    </div>
                    <p className="text-primary-dark text-sm">
                      An invitation email will be sent to <strong>{emailInput}</strong>.
                      {invitationPreview.advisorExists
                        ? ' They can click accept in the email to join your subscription.'
                        : ' They must create an account using this exact email address to receive the subscription.'}
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <div className="flex w-full justify-between">
                  <div className="flex gap-2">
                    {/* Wizard Direct Assignment - Only show if advisor exists */}
                    {isWizardUser && invitationPreview.advisorExists && (
                      <Button
                        onClick={onDirectAssignSeat}
                        disabled={isMutating && currentMutation === 'direct_assign'}
                        variant="ghost"
                        className="text-primary-dark"
                      >
                        <Wand2 className="mr-1 h-4 w-4" />
                        {isMutating && currentMutation === 'direct_assign'
                          ? 'Assigning...'
                          : 'Assign Directly'}
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowPreview(false)}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={onSendInvitation}
                      disabled={isMutating && currentMutation === 'invite'}
                    >
                      <Send className="mr-1 h-4 w-4" />
                      {isMutating && currentMutation === 'invite'
                        ? 'Sending...'
                        : 'Send Invitation'}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Remove Seat Dialog */}
      <Dialog
        open={dialogType === 'removeSeat'}
        onOpenChange={onClose}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Remove Seat</DialogTitle>
            <DialogDescription className="text-gray-700">
              Are you sure you want to remove this seat? If the seat is active, the advisor will
              lose access and your subscription quantity will be decreased. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onRemoveSeat}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
